import { AdminGuard } from "@/admin/components/admin-guard";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, edenFetch } from "@/lib/eden";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, Gift } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Admin gift-card ledger (PRD 10 §7) — the "where is my gift card" screen.
 *
 * Legacy had nothing here: support read MySQL by hand. The list is deliberately
 * searchable by the things a member actually quotes in a ticket — their email,
 * the partner order id, or the id from a URL.
 */

export type GiftStatus =
  | "pending"
  | "ordered"
  | "issued"
  | "delivered"
  | "redeemed"
  | "canceled"
  | "expired";

export type LedgerRow = {
  gift_card_id: string;
  status: GiftStatus;
  provider: string;
  provider_order_ref: string | null;
  brand_name: string | null;
  amount_minor: number | null;
  currency_code: string | null;
  issued_at: string | null;
  delivered_at: string | null;
  redeemed_at: string | null;
  created_at: string;
  legacy_ref: string | null;
  user_id: string | null;
  email: string | null;
  display_name: string | null;
};

export function money(amountMinor: number | null, currencyCode: string | null): string {
  if (amountMinor == null) return "—";
  const major = amountMinor / 100;
  const amount = Number.isInteger(major) ? String(major) : major.toFixed(2);
  const code = (currencyCode ?? "USD").toUpperCase();
  return code === "USD" ? `$${amount}` : `${amount} ${code}`;
}

/**
 * Status tone. `ordered` is amber on purpose, not neutral: in this integration
 * it means "the browser claimed an order the partner never confirmed", which is
 * either harmless or a lost webhook against real money — and the only way to
 * tell is to look.
 */
export function statusTone(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "issued" || status === "delivered" || status === "redeemed") return "default";
  if (status === "canceled" || status === "expired") return "destructive";
  if (status === "ordered") return "outline";
  return "secondary";
}

export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const STATUSES: GiftStatus[] = [
  "pending",
  "ordered",
  "issued",
  "delivered",
  "redeemed",
  "canceled",
  "expired",
];

export function isGiftStatus(value: unknown): value is GiftStatus {
  return STATUSES.includes(value as GiftStatus);
}

export const GIFT_LIST_LIMIT = 100;

/**
 * The ledger query. Exported because gift-card-detail re-runs the very same one to work
 * out a gift's Previous / Next — same key, so it usually answers from cache.
 */
export function giftCardsListQuery(q: string | undefined, status: GiftStatus | undefined) {
  return {
    queryKey: ["/api/admin/gift-cards", q ?? "", status ?? ""],
    queryFn: () =>
      edenFetch(
        api.api.admin["gift-cards"].get({
          query: {
            ...(q ? { q } : {}),
            ...(status ? { status } : {}),
            limit: GIFT_LIST_LIMIT,
          },
        }),
      ) as Promise<{ items: LedgerRow[]; total: number }>,
  };
}

export default function AdminGiftCardsPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/admin/gift-cards/" });
  const statusFilter = search.status ?? "";

  // Search text and status live in the URL, so a refresh, Back or a pasted link
  // reproduces the ledger — and the detail page can rebuild it for Previous / Next.
  // Typing stays local; the debounced copy is what the URL carries.
  const [term, setTerm] = useState(search.q ?? "");
  const [debounced, setDebounced] = useState(term);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 400);
    return () => clearTimeout(t);
  }, [term]);
  useEffect(() => {
    const q = debounced.trim() || undefined;
    // A same-URL replace on mount raced the non-admin redirect off /admin into a render loop.
    if (q === search.q) return;
    void navigate({
      from: "/admin/gift-cards/",
      to: "/admin/gift-cards",
      search: (prev) => ({ ...prev, q }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  useEffect(() => {
    // Adopt the URL only when it really differs from what's typed (ignoring a trailing
    // space the trim dropped), or the round-trip eats the space mid-word.
    setTerm((t) => (t.trim() === (search.q ?? "") ? t : (search.q ?? "")));
  }, [search.q]);

  const setStatusFilter = (next: GiftStatus | "") =>
    void navigate({
      to: "/admin/gift-cards",
      search: (prev) => ({ ...prev, status: next || undefined }),
    });

  const { data, isLoading } = useQuery(giftCardsListQuery(search.q, search.status));

  // The drift report: the two categories that can mean a member paid and got
  // nothing. Surfaced as a banner rather than buried in a job log.
  const { data: health } = useQuery({
    queryKey: ["/api/admin/gift-cards-health"],
    queryFn: () =>
      edenFetch(api.api.admin["gift-cards-health"].get()) as Promise<{
        stuckOrdered: number;
        missingAssets: number;
      }>,
  });

  const rows = data?.items ?? [];
  const drift = (health?.stuckOrdered ?? 0) + (health?.missingAssets ?? 0);

  // What a row hands the detail page so it can rebuild this ledger for Previous / Next.
  // `lstatus` is always sent ("all" = unfiltered), so its presence is the marker that
  // says "you arrived from the list".
  const listSearch = { lq: search.q, lstatus: statusFilter || ("all" as const) };

  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <div className="border-b px-6 h-14 shrink-0 flex items-center justify-between">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Gift Cards
          </h1>
          {data ? <span className="text-sm text-muted-foreground">{data.total} total</span> : null}
        </div>

        {drift > 0 ? (
          <div className="mx-6 mt-4 flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
            <div>
              <p className="font-medium">
                {health?.stuckOrdered ? `${health.stuckOrdered} order(s) unconfirmed` : null}
                {health?.stuckOrdered && health?.missingAssets ? " · " : null}
                {health?.missingAssets
                  ? `${health.missingAssets} issued without a redemption link`
                  : null}
              </p>
              <p className="text-muted-foreground">
                Both mean a partner webhook never arrived. There is no order-lookup endpoint, so
                these can't self-heal from Jifiti — check the member's gift and resend or annotate.
              </p>
            </div>
          </div>
        ) : null}

        <div className="px-6 py-4 shrink-0 flex items-center gap-2 flex-wrap">
          <SearchInput
            containerClassName="max-w-md"
            placeholder="Member email or name, order ref, gift id…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            data-testid="input-gift-cards-search"
          />
          <div className="flex items-center gap-1 flex-wrap">
            <Badge
              variant={statusFilter === "" ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => setStatusFilter("")}
            >
              All
            </Badge>
            {STATUSES.map((s) => (
              <Badge
                key={s}
                variant={statusFilter === s ? "default" : "outline"}
                className="cursor-pointer capitalize"
                onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                data-testid={`filter-gift-status-${s}`}
              >
                {s}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 pb-6">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="gift"
              title="No gift cards"
              message={
                search.q || statusFilter
                  ? "Nothing matches that search."
                  : "Gift cards appear here once members start buying them."
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Order ref</TableHead>
                  <TableHead>Bought</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.gift_card_id} data-testid={`row-gift-${r.gift_card_id}`}>
                    <TableCell>
                      <Link
                        to="/admin/gift-cards/$giftCardId"
                        params={{ giftCardId: r.gift_card_id }}
                        search={listSearch}
                        className="hover:underline"
                      >
                        <span className="font-medium">{r.display_name ?? "—"}</span>
                        <span className="block text-xs text-muted-foreground">
                          {r.email ?? "—"}
                        </span>
                      </Link>
                    </TableCell>
                    <TableCell>{r.brand_name ?? "—"}</TableCell>
                    <TableCell>{money(r.amount_minor, r.currency_code)}</TableCell>
                    <TableCell>
                      <Badge variant={statusTone(r.status)} className="capitalize">
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.provider_order_ref ?? (
                        <span className="text-muted-foreground">not yet confirmed</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {shortDate(r.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </AdminGuard>
  );
}
